import React from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import Chip from '@material-ui/core/Chip';
import Countdown from 'react-countdown';
import format from 'date-fns/format';
import pluralize from 'pluralize';
import clsx from 'clsx';

import { getGigOwner, getMilestoneDates, safeDateFormat } from 'utils/helpers';
import { openModal } from 'features/utilsSlice';
import VideoIcon from 'public/icons/video.svg';
import Video from 'components/Video';
import PaymentChargeBanner from 'components/milestones/PaymentChargeBanner';
import styles from 'styles/components/milestones/Milestones.module.scss';

const videoStyle = { display: 'flex', width: '100%', margin: '0 auto' };
const dateStyle = { fontSize: 13, fontFamily: 'Avenir-Roman' };

const Milestone = ({ milestone, setSelectedMilestoneId, gig, gigUserId, gigStatus, isPreview }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.auth.currentUser);
  const gigOwner = getGigOwner(currentUser, gigUserId);

  const cancelledGig = gigStatus === 'cancelled' && !milestone.initial_screenshot.url;
  const assetsLength = milestone.assets.length;

  const getWeekInterval = (startDate, endDate) => {
    const currentYear = new Date().getFullYear();
    const startFormat = startDate.getFullYear() === currentYear ? 'MMM dd' : 'MMM dd yyyy';
    const endFormat = endDate.getFullYear() === currentYear ? 'MMM dd' : 'MMM dd yyyy';

    try {
      return `${format(startDate, startFormat)} - ${format(endDate, endFormat)}`;
    } catch {
      return '';
    }
  };

  const getToBePostedWeek = () => {
    if (milestone.guaranteed_gains_current) {
      return (
        <span style={{ fontFamily: 'Avenir Medium', fontSize: 14 }}>
          {milestone.guaranteed_gains_current} of {milestone.guaranteed_gains_max} subs delivered
        </span>
      );
    }

    if (!milestone.latest_start_time) return 'TBD';

    if (milestone.event_attendance)
      return format(new Date(milestone.delivery_by), 'MMM d, yyyy, HH:mm O');

    const { start, end, interval, label } = getMilestoneDates(
      milestone.earliest_start_time,
      milestone.latest_start_time
    );

    if (new Date() > new Date(milestone.latest_start_time)) {
      return (
        <span style={{ fontFamily: 'Avenir Medium', fontSize: 14, color: '#ff4b4b' }}>
          {milestone.guaranteed_gains ? 'In Progress' : 'Overdue'}
        </span>
      );
    }

    if (interval) {
      return (
        <div style={{ marginTop: 2 }}>
          To be posted{' '}
          <span
            style={
              label === 'This week'
                ? { fontFamily: 'Avenir Medium', fontSize: 14, color: '#ff4b4b' }
                : { fontFamily: 'Avenir Medium', fontSize: 14 }
            }
          >
            {label.toLowerCase()}
          </span>{' '}
          ({getWeekInterval(start, end)})
        </div>
      );
    } else {
      return `To be posted by ${safeDateFormat(
        new Date(milestone.latest_start_time),
        'MMM d, yyyy',
        ''
      )}`;
    }
  };

  const getBackgroundColor = () => {
    if (milestone.status === 'approved') {
      return { opacity: 0.5 };
    } else if (milestone.status === 'awaiting_approval') {
      return { backgroundColor: '#f5f0ff' };
    }
  };

  const getLabel = () => {
    if (milestone.status === 'approved') {
      return 'Approved & Paid';
    } else if (milestone.status === 'awaiting_approval') {
      return gigOwner ? 'Needs approval' : 'Awaiting approval';
    } else if (milestone.status === 'rejected') {
      return 'Not accepted';
    } else if (milestone.status === 'approve_attempting_payment') {
      return 'Payment processing';
    } else if (cancelledGig) {
      return 'Cancelled';
    }

    return 'Posted';
  };

  const getDate = () => {
    if (milestone.status === 'awaiting_approval') {
      return format(new Date(milestone.delivered_at), 'MMM d, yyyy');
    } else if (milestone.status === 'approved') {
      return format(new Date(milestone.approved_at), 'MMM d, yyyy');
    } else if (milestone.status === 'pending' && milestone.expires_at) {
      return format(new Date(milestone.posted_at), 'MMM d, yyyy');
    } else if (milestone.status === 'approve_attempting_payment') {
      return '';
    } else if (cancelledGig) {
      return '/';
    } else {
      return format(new Date(milestone.rejected_at), 'MMM d, yyyy');
    }
  };

  return (
    <div
      className={clsx(styles.milestone, { [styles.noHover]: isPreview })}
      style={getBackgroundColor()}
      onClick={() => {
        if (isPreview) return;
        setSelectedMilestoneId(milestone.id);
        dispatch(openModal({ name: 'Milestone' }));
      }}
    >
      <div className={styles.assetImg}>
        {milestone.assets[0]?.video ? (
          <>
            <div
              className="video-container"
              onClick={() => {
                if (isPreview) return;
                setSelectedMilestoneId(milestone.id);
                dispatch(openModal({ name: 'Milestone' }));
              }}
            />

            {milestone.assets[0].content_type !== 'mp4' ? (
              <VideoIcon style={videoStyle} width={60} height={60} />
            ) : (
              <Video src={milestone.assets[0].url} />
            )}
          </>
        ) : (
          <Image
            src={assetsLength > 0 ? milestone.assets[0].url : milestone.inventory_space.icon_url}
            layout="fill"
            alt={assetsLength > 0 ? 'Asset' : 'Inventory Space'}
            unoptimized
          />
        )}

        {assetsLength > 1 && (
          <Chip color="secondary" label={`+${assetsLength - 1}`} className="asset-chip" />
        )}
      </div>

      <div className={styles.networkImg}>
        <Image
          src={milestone.network.active_icon_url}
          width={30}
          height={30}
          alt={milestone.network.name}
          unoptimized
        />
      </div>

      <div className={styles.milestoneDate}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
          <div>
            <h2>{milestone.name}</h2>

            {gigStatus === 'pending' &&
              (milestone.delivery_by ? (
                <span style={dateStyle}>
                  To be delivered by{' '}
                  {format(new Date(milestone.delivery_by), 'MMM d, yyyy, HH:mm O')}
                </span>
              ) : (
                <span style={dateStyle}>TBD</span>
              ))}

            {milestone.expires_at && milestone.status === 'pending' && !milestone.delivered_at && (
              <Countdown
                date={new Date(milestone.expires_at)}
                intervalDelay={60000}
                renderer={({ days, hours, minutes }) => {
                  if (days >= 2 && hours >= 0) {
                    return `Live: ${days} days ${hours > 0 &&
                      `${hours} ${pluralize('hours', hours)}`} left`;
                  } else if (hours > 0 && days < 2) {
                    const dayToHours = days * 24;
                    return `Live: ${hours + dayToHours} ${pluralize('hours', hours)} left`;
                  } else if (minutes > 0 && hours === 0) {
                    return `Live: ${minutes} ${pluralize('minutes', minutes)} left`;
                  } else {
                    return `${gigOwner ? 'Waiting for delivery' : 'Time to deliver'}`;
                  }
                }}
              />
            )}
          </div>

          {gigStatus !== 'pending' &&
            (milestone.status !== 'pending' ||
              (milestone.status === 'pending' && milestone.expires_at)) && (
              <div className={styles.badgeWithDate}>
                <Chip
                  label={getLabel()}
                  className={clsx('light-green-chip', {
                    ['red-chip']: milestone.rejected_at || cancelledGig,
                    ['purple-chip']: milestone.status === 'awaiting_approval',
                  })}
                />
                {getDate()}
              </div>
            )}
        </div>

        {milestone.status === 'pending' && !milestone.expires_at && getToBePostedWeek()}

        {milestone.status === 'awaiting_approval' && <div className={styles.redDot} />}

        {gigOwner && milestone.status === 'approve_attempting_payment' && gig && (
          <PaymentChargeBanner milestone={milestone} gig={gig} inline />
        )}
      </div>
    </div>
  );
};

Milestone.propTypes = {
  gigUserId: PropTypes.number.isRequired,
  gigStatus: PropTypes.string.isRequired,
  milestone: PropTypes.object.isRequired,
  setSelectedMilestoneId: PropTypes.func.isRequired,
  gig: PropTypes.object,
  isPreview: PropTypes.bool,
};

Milestone.defaultProps = {
  gig: null,
  isPreview: false,
};

export default Milestone;
